import React, { Children, useEffect, useRef, useState } from "react";

import { StackShim } from "@fluentui/react-migration-v8-v9";

import i18n from "i18next";

import {
  Button,
  Image,
  Input,
  Link,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  TableCellLayout,
  TableColumnDefinition,
  Tag,
  Text,
  Tooltip,
  createTableColumn,
} from "@fluentui/react-components";
import { IUserProduct } from "../../Models/IUserProduct";
import { baseUrl } from "../../helpers/constants";

import { getHighlightedText } from "../../Controls/HighlightedText";
import {
  CameraAddRegular,
  DeleteRegular,
  LockClosedRegular,
  MoreVerticalRegular,
  PeopleTeamRegular,
  ShoppingBagAddRegular,
} from "@fluentui/react-icons";
import AddToBedarfDialog from "../Account/Bedarfe/AddToBedarfDialog";
import DeleteUserProductDialog from "./DeleteUserProduct";
import FreigabeDialog from "../FreigabeDialog/FreigabeDialog";
import { updateUserProduct } from "./userProductsService";
import { fetchWrapper } from "../../helpers/fetchwrapper";
import ReactQuill from "react-quill";
import "quill/dist/quill.bubble.css";
import "./UserProductsListColumnsOverrideQuill.css";
import {
  modulesForTechnicalData,
  formatsForTechnicalData,
} from "../../helpers/reactQuillConstants";

interface IUserProductsListColumnsProps {
  isEditModeOn: boolean;
  isPartnerLounge: boolean;
  textFilter: string;
  foundByToProductList: { userProductGuid: string; foundBy: string[] }[];
  onSelectProduct: (
    guid: string,
    action: "delete" | "freigabe" | "openDetails" | "addToBedarf",
  ) => void;
  onRefresh(): void;
}

export function UserProductsListColumns(props: IUserProductsListColumnsProps) {
  const [addToBedarfGuid, setAddToBedarfGuid] = useState<string | undefined>(
    undefined,
  );

  const [deleteGuid, setDeleteGuid] = useState<string | undefined>(undefined);

  const [freigabeGuid, setFreigabeGuid] = useState<string | undefined>(
    undefined,
  );

  useEffect(() => {
    if (!addToBedarfGuid) {
      props.onRefresh();
    } else {
      props.onSelectProduct(addToBedarfGuid, "addToBedarf");
    }
  }, [addToBedarfGuid]);

  useEffect(() => {
    if (!deleteGuid) {
      props.onRefresh();
    } else {
      props.onSelectProduct(deleteGuid, "delete");
    }
  }, [deleteGuid]);

  useEffect(() => {
    if (!freigabeGuid) {
      props.onRefresh();
    } else {
      props.onSelectProduct(freigabeGuid, "freigabe");
    }
  }, [freigabeGuid]);

  // --- Für den Foto upload Button
  const inputFile = useRef<any>(null);

  const onSelectedFile = (event: any, userProduktGuid: string) => {
    uploadFile(event.target.files, userProduktGuid);
  };

  const uploadFile = (files: any[], userProduktGuid: string) => {
    if (files != undefined && userProduktGuid) {
      for (let i = 0; i < files.length; i++) {
        var data = new FormData();
        data.append("file", files[i]);
        fetchWrapper
          .sendFilesWithAuth(
            `${baseUrl}/api/UserMedia/UploadFile?id=${userProduktGuid}&name=${files[i].name}`,
            data,
          )
          .then((result: any) => {
            setAsVorschaubild(userProduktGuid, result.userObjectGuid);
          });
      }
    }
  };
  const setAsVorschaubild = (
    userProduktGuid: string,
    userMediaGuid: string,
  ) => {
    fetchWrapper
      .getWithAuth(
        `${baseUrl}/api/UserProduct/SetVorschauPicture?UserProduktGuid=${userProduktGuid}&userMediaGuid=${userMediaGuid}`,
      )
      .then((result) => {
        props.onRefresh();
      });
  };
  const [currentFocusedCell, setCurrentFocusedCell] = useState<string>("");
  //----

  let columns: TableColumnDefinition<IUserProduct>[] = [
    //{i18n.t<string>("vorschauPic")}
    createTableColumn<IUserProduct>({
      columnId: "vorschauPic",
      renderHeaderCell: () => {
        return i18n.t<string>("vorschauPic");
      },
      renderCell: (item: IUserProduct) => {
        return (
          <TableCellLayout
            truncate
            style={{ display: "flex", justifyContent: "center" }}
          >
            {item.vorschauPicPath != undefined &&
            !item.vorschauPicPath.includes("DefaultImage") ? (
              <Tooltip
                withArrow
                positioning={"below-end"}
                content={{
                  children: (
                    <Image
                      alt="Vorschau"
                      src={`${baseUrl}/api/UserMedia/GetMedia?id=${item.vorschauPicGuid}`}
                    />
                  ),
                  style: { maxWidth: 500, maxHeight: 500 },
                }}
                relationship={"description"}
              >
                <Image
                  height={40}
                  alt="Vorschau"
                  src={`${baseUrl}/api/UserMedia/GetMedia?id=${
                    item.vorschauPicGuid
                      ? item.vorschauPicGuid
                      : "544b1221-8eef-4a44-adf9-7bd6a4724ea7"
                  }`}
                />
              </Tooltip>
            ) : (
              <>
                <input
                  title="uploadFile"
                  type="file"
                  id="file"
                  multiple
                  accept="image/png, image/jpeg, image/jpg"
                  ref={inputFile}
                  className="lueb-hide"
                  onChange={(ev) => {
                    onSelectedFile(ev, item.userProduktGuid);
                  }}
                />
                <Button
                  icon={<CameraAddRegular />}
                  appearance="transparent"
                  onClick={() => {
                    if (inputFile && inputFile.current) {
                      inputFile.current.click();
                    }
                  }}
                />
              </>
            )}
          </TableCellLayout>
        );
      },
    }),
    createTableColumn<IUserProduct>({
      columnId: "systemId",
      renderHeaderCell: () => {
        return i18n.t<string>("systemId");
      },
      renderCell: (item: IUserProduct) => {
        return (
          <TableCellLayout truncate>
            <Link
              onClick={() => {
                props.onSelectProduct(item.userProduktGuid, "openDetails");
              }}
            >
              {getHighlightedText(item.seriennummer, props.textFilter)}
            </Link>
          </TableCellLayout>
        );
      },
    }),
    createTableColumn<IUserProduct>({
      columnId: "benennung",
      renderHeaderCell: () => {
        return i18n.t<string>("benennung");
      },
      renderCell: (item: IUserProduct) => {
        return (
          <TableCellLayout truncate>
            <div className="lueb-centerVertical">
              <StackShim horizontal tokens={{ childrenGap: 10 }}>
                <Text>
                  {getHighlightedText(
                    i18n.language.includes("de")
                      ? item.benennungDE
                      : item.benennungEN,
                    props.textFilter,
                  )}
                </Text>
              </StackShim>
            </div>
          </TableCellLayout>
        );
      },
    }),
    createTableColumn<IUserProduct>({
      columnId: "actions",
      renderHeaderCell: () => {
        return i18n.t<string>("actions");
      },
      renderCell: (item: IUserProduct) => {
        return (
          <TableCellLayout truncate>
            {item.canCreateFreigabe && (
              <Tooltip
                relationship="label"
                content={i18n.t<string>("shareObject")!}
              >
                <Button
                  appearance="subtle"
                  icon={
                    item.hasFreigaben ? (
                      <PeopleTeamRegular color="#ff6600" />
                    ) : (
                      <LockClosedRegular color="#ff6600" />
                    )
                  }
                  onClick={() => {
                    setFreigabeGuid(item.userProduktGuid);
                  }}
                ></Button>
              </Tooltip>
            )}
            {item.canEditObject && (
              <Menu>
                <MenuTrigger disableButtonEnhancement>
                  <MenuButton
                    icon={<MoreVerticalRegular color="#ff6600" />}
                    appearance="subtle"
                  ></MenuButton>
                </MenuTrigger>
                <MenuPopover>
                  <MenuList>
                    <MenuItem
                      id="addToBedarf"
                      icon={<ShoppingBagAddRegular />}
                      onClick={() => {
                        setAddToBedarfGuid(item.userProduktGuid);
                      }}
                    >
                      {i18n.t<string>("addToBedarf")}
                    </MenuItem>

                    <MenuItem
                      id="delete"
                      disabled={!item.canDeleteObject}
                      icon={<DeleteRegular />}
                      onClick={() => {
                        setDeleteGuid(item.userProduktGuid);
                      }}
                    >
                      {i18n.t<string>("delete")}
                    </MenuItem>
                  </MenuList>
                </MenuPopover>
              </Menu>
            )}
          </TableCellLayout>
        );
      },
    }),

    createTableColumn<IUserProduct>({
      columnId: "userProductNumber",
      renderHeaderCell: () => {
        return i18n.t<string>("userProductNumber");
      },
      renderCell: (item: IUserProduct) => {
        return (
          <TableCellLayout truncate>
            <div className="lueb-centerVertical">
              <StackShim horizontal tokens={{ childrenGap: 10 }}>
                {!props.isEditModeOn && (
                  <Text>
                    {getHighlightedText(
                      item.kundenProduktNummer,
                      props.textFilter,
                    )}
                  </Text>
                )}

                {props.isEditModeOn && (
                  <Input
                    defaultValue={item.kundenProduktNummer}
                    onBlur={(ev) => {
                      item.kundenProduktNummer = ev.target.value;
                      updateUserProduct(item);
                    }}
                  />
                )}
              </StackShim>
            </div>
          </TableCellLayout>
        );
      },
    }),
    createTableColumn<IUserProduct>({
      columnId: "userProductText",
      renderHeaderCell: () => {
        return i18n.t<string>("userProductText");
      },
      renderCell: (item: IUserProduct) => {
        var textField = document.getElementById(
          `kundenProdTextDIV-${item.userProduktGuid}`,
        );
        if (textField) {
          textField.innerHTML = item.kundenProdukttext;
        }
        return (
          <TableCellLayout truncate={!props.isEditModeOn}>
            <div className="lueb-centerVertical">
              {props.isEditModeOn ? (
                <ReactQuill
                  className="my-quill-wrapper"
                  style={{
                    minWidth: "150px",
                    maxWidth: "300px",
                    zIndex: item.userProduktGuid == currentFocusedCell ? 1 : 0,
                    border: "1px solid #d0d0d0",
                    borderBottomColor: "black",
                    borderRadius: "3px",
                    padding: "5px",
                  }}
                  modules={modulesForTechnicalData}
                  formats={formatsForTechnicalData}
                  value={item.kundenProdukttext}
                  onFocus={() => {
                    setCurrentFocusedCell(item.userProduktGuid);
                  }}
                  onChange={(value: string) => {
                    item.kundenProdukttext = value;
                  }}
                  onBlur={() => {
                    updateUserProduct(item);
                  }}
                  theme="bubble"
                />
              ) : (
                <div id={`kundenProdTextDIV-${item.userProduktGuid}`}></div>
              )}
            </div>
          </TableCellLayout>
        );
      },
    }),
    createTableColumn<IUserProduct>({
      columnId: "updatedOn",
      renderHeaderCell: () => {
        return i18n.t<string>("changeDate");
      },
      renderCell: (item: IUserProduct) => {
        return (
          <TableCellLayout truncate>
            <div className="lueb-centerVertical">
              <StackShim horizontal tokens={{ childrenGap: 10 }}>
                <Text>
                  {item.updatedOn
                    ? new Date(item.updatedOn).toLocaleDateString()
                    : item.createdOn
                      ? new Date(item.createdOn).toLocaleDateString()
                      : ""}
                </Text>
              </StackShim>
            </div>
          </TableCellLayout>
        );
      },
    }),
    //kundenProduktNummer
  ];

  if (props.isPartnerLounge) {
    // Auftragsnummer muss eingefügt werden
    const auftragColumn = createTableColumn<IUserProduct>({
      columnId: "auftragId",
      renderHeaderCell: () => {
        return i18n.t<string>("auftragId");
      },
      renderCell: (item: IUserProduct) => {
        return (
          <TableCellLayout truncate>
            <div className="lueb-centerVertical">
              <StackShim horizontal tokens={{ childrenGap: 10 }}>
                {!props.isEditModeOn && (
                  <Text>
                    {getHighlightedText(item.auftragId, props.textFilter)}
                  </Text>
                )}
              </StackShim>
            </div>
          </TableCellLayout>
        );
      },
    });

    columns.splice(4, 0, auftragColumn);
  }

  /*   if (props.foundByToProductList.length != 0) {
    columns.push(
      createTableColumn<IUserProduct>({
        columnId: "foundTag",
        renderHeaderCell: () => i18n.t<string>("foundBy"),
        renderCell: (item: IUserProduct) => (
          <TableCellLayout truncate>
            {props.foundByToProductList
              .find((x) => item.userProduktGuid == x.userProductGuid)
              ?.foundBy.map((x) => (
                <Tag>{x}</Tag>
              ))}
          </TableCellLayout>
        ),
      }),
    );
  } */

  return columns;
}
